import { Button } from '@app/app/_components/Button/Button';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Select } from '@app/app/_components/Input/Select/Select';
import { SideSheet } from '@app/app/_components/SideSheet/SideSheet';
import { ArrowLeftIcon } from '@app/app/_icons/ArrowLeftIcon';
import { CheckThinIcon } from '@app/app/_icons/CheckThinIcon';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { BeneficiaryData } from '@app/domain/model/policy/BeneficiaryData';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { getPercentageOptions } from '@app/app/self-service/(wizard)/change-beneficiary-death/utils/getPercentageOptions';

interface Props {
    isOpen: boolean;
    editingBeneficiary: Nullable<BeneficiaryData>;
    maxPermittedPercentage: number;
    currentPercentage: string;
    currentBeneficiaryShare: number;
    onClose: () => void;
    onSave: (beneficiaryId: string, percentage: string) => void;
}

export function BeneficiaryShareEditor({
    isOpen,
    editingBeneficiary,
    maxPermittedPercentage,
    currentPercentage,
    currentBeneficiaryShare,
    onClose,
    onSave,
}: Props) {
    const t = useTranslations();
    const [shareValue, setShareValue] = useState('');

    const percentageOptions = useMemo(
        () => getPercentageOptions(maxPermittedPercentage, currentBeneficiaryShare),
        [maxPermittedPercentage, currentBeneficiaryShare],
    );

    const availableOptions = percentageOptions;

    const handleOpen = useCallback(() => {
        setShareValue(currentPercentage);
    }, [currentPercentage]);

    const handleClose = useCallback(() => {
        setShareValue('');
        onClose();
    }, [onClose]);

    const handleSave = useCallback(() => {
        if (!editingBeneficiary || shareValue === '') return;

        if (shareValue === currentPercentage) {
            handleClose();
            return;
        }

        onSave(editingBeneficiary.id, shareValue);
        handleClose();
    }, [editingBeneficiary, shareValue, currentPercentage, onSave, handleClose]);

    const handleShareChange = useCallback((value: string) => {
        setShareValue(value);
    }, []);

    useMemo(() => {
        if (isOpen && currentPercentage) {
            handleOpen();
        }
    }, [isOpen, currentPercentage, handleOpen]);

    const header = (
        <div className="flex items-center gap-3 rounded-t-xl border-b border-neutral-300 px-6 py-4">
            <button className="cursor-pointer" onClick={handleClose} aria-label={t('general.back')}>
                <ArrowLeftIcon className="h-5 w-5 text-neutral-500" />
            </button>
            <Heading level="h3">
                {editingBeneficiary ? `${editingBeneficiary.firstName} ${editingBeneficiary.lastName}` : ''}
            </Heading>
        </div>
    );

    const footer = (
        <div className="flex w-full flex-row items-center gap-6 p-6">
            <Button
                onClick={handleClose}
                className="w-full"
                variant="outlined"
                rightAddon={<CloseIcon className="size-5" />}>
                {t('general.cancel')}
            </Button>
            <Button
                className="w-full"
                rightAddon={<CheckThinIcon className="size-5" />}
                onClick={handleSave}
                preventDefault
                disabled={!editingBeneficiary || shareValue === ''}>
                {t('general.save')}
            </Button>
        </div>
    );

    return (
        <SideSheet
            header={header}
            open={isOpen}
            footer={footer}
            contentClassName="w-full md:w-[450px] bg-neutral-50 p-6">
            <Select
                label={t('general.percentage')}
                name="share"
                value={shareValue}
                onChange={handleShareChange}
                options={availableOptions}
            />
        </SideSheet>
    );
}
